/**
 * Directory / RBAC / onboarding domain event definitions — catalog 1.1.0
 * additions (commerce-platform Phase 1, plan §5 / Step 7, task T02).
 *
 * Producer: `services/organizations` (phase Step 6) — the file groups the
 * three Step-6 event families because Step 7 names exactly three Phase-1
 * catalog files (`identity.ts`, `customers.ts`, `directory.ts`):
 * - `directory:*`  — Store/Workspace provisioning (vocabulary lock P1-A5:
 *   organization = tenant, store = physical location, workspace = operational
 *   unit inside a store).
 * - `roles:*`      — the capability-based simple-role compiler output.
 * - `onboarding:*` — the tenant/store onboarding saga terminal outcomes
 *   (triggered by the EXACT published name `organizations:org.created`; that
 *   legacy event keeps its frozen name per master A4 and is not re-registered
 *   here).
 * All are `auditable: true` (phase §5).
 */
import {
  defineEvent,
  asEventRecord,
  optionalString,
  requireString,
  requireStringArray,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface StoreCreatedPayload {
  storeId: string;
  orgId: string;
  name: string;
  /** ISO-4217 alpha-3 currency set on the org's first store (immutable after first order, OQ-12). */
  currency: string;
  /** IANA timezone of the store. */
  timezone: string;
}

export interface WorkspaceCreatedPayload {
  workspaceId: string;
  orgId: string;
  storeId: string;
  /** Workspace kind, e.g. `retail | cinema | events` (extensible). */
  kind: string;
}

export interface SimpleRoleCreatedPayload {
  /** Role id returned by the roles service for the compiled policy set. */
  roleId: string;
  orgId: string;
  name: string;
  /** Capability dictionary entries the compiler was asked to encode. */
  capabilities: string[];
}

export interface OnboardingJobCompletedPayload {
  jobId: string;
  orgId: string;
  /** Default store produced by the saga. */
  storeId: string;
  /** Default retail workspace produced by the saga. */
  workspaceId: string;
}

export interface OnboardingJobFailedPayload {
  jobId: string;
  orgId: string;
  error: string;
  /** Saga step that failed (compensation ran from this point backwards). */
  step?: string;
}

const storeCreatedSchema: EventSchema<StoreCreatedPayload> = {
  parse(data: unknown): StoreCreatedPayload {
    const record = asEventRecord(data, 'directory:store-created');
    return {
      storeId: requireString(record.storeId, 'storeId', 'directory:store-created'),
      orgId: requireString(record.orgId, 'orgId', 'directory:store-created'),
      name: requireString(record.name, 'name', 'directory:store-created'),
      currency: requireString(record.currency, 'currency', 'directory:store-created'),
      timezone: requireString(record.timezone, 'timezone', 'directory:store-created'),
    };
  },
};

const workspaceCreatedSchema: EventSchema<WorkspaceCreatedPayload> = {
  parse(data: unknown): WorkspaceCreatedPayload {
    const record = asEventRecord(data, 'directory:workspace-created');
    return {
      workspaceId: requireString(record.workspaceId, 'workspaceId', 'directory:workspace-created'),
      orgId: requireString(record.orgId, 'orgId', 'directory:workspace-created'),
      storeId: requireString(record.storeId, 'storeId', 'directory:workspace-created'),
      kind: requireString(record.kind, 'kind', 'directory:workspace-created'),
    };
  },
};

const simpleRoleCreatedSchema: EventSchema<SimpleRoleCreatedPayload> = {
  parse(data: unknown): SimpleRoleCreatedPayload {
    const record = asEventRecord(data, 'roles:simple-role-created');
    return {
      roleId: requireString(record.roleId, 'roleId', 'roles:simple-role-created'),
      orgId: requireString(record.orgId, 'orgId', 'roles:simple-role-created'),
      name: requireString(record.name, 'name', 'roles:simple-role-created'),
      capabilities: requireStringArray(record.capabilities, 'capabilities', 'roles:simple-role-created'),
    };
  },
};

const onboardingJobCompletedSchema: EventSchema<OnboardingJobCompletedPayload> = {
  parse(data: unknown): OnboardingJobCompletedPayload {
    const record = asEventRecord(data, 'onboarding:job-completed');
    return {
      jobId: requireString(record.jobId, 'jobId', 'onboarding:job-completed'),
      orgId: requireString(record.orgId, 'orgId', 'onboarding:job-completed'),
      storeId: requireString(record.storeId, 'storeId', 'onboarding:job-completed'),
      workspaceId: requireString(record.workspaceId, 'workspaceId', 'onboarding:job-completed'),
    };
  },
};

const onboardingJobFailedSchema: EventSchema<OnboardingJobFailedPayload> = {
  parse(data: unknown): OnboardingJobFailedPayload {
    const record = asEventRecord(data, 'onboarding:job-failed');
    return {
      jobId: requireString(record.jobId, 'jobId', 'onboarding:job-failed'),
      orgId: requireString(record.orgId, 'orgId', 'onboarding:job-failed'),
      error: requireString(record.error, 'error', 'onboarding:job-failed'),
      step: optionalString(record.step, 'step', 'onboarding:job-failed'),
    };
  },
};

export const DIRECTORY_STORE_CREATED_EVENT: EventDefinition<StoreCreatedPayload> =
  defineEvent<StoreCreatedPayload>({
    type: 'directory:store-created',
    version: 1,
    schema: storeCreatedSchema,
    auditable: true,
    description: 'A store (physical location) was created for an org; first-store creation also fixes the org currency.',
    since: '1.1.0',
  });

export const DIRECTORY_WORKSPACE_CREATED_EVENT: EventDefinition<WorkspaceCreatedPayload> =
  defineEvent<WorkspaceCreatedPayload>({
    type: 'directory:workspace-created',
    version: 1,
    schema: workspaceCreatedSchema,
    auditable: true,
    description: 'A workspace (operational unit inside a store, e.g. retail floor, cinema room-ops) was created.',
    since: '1.1.0',
  });

export const ROLES_SIMPLE_ROLE_CREATED_EVENT: EventDefinition<SimpleRoleCreatedPayload> =
  defineEvent<SimpleRoleCreatedPayload>({
    type: 'roles:simple-role-created',
    version: 1,
    schema: simpleRoleCreatedSchema,
    auditable: true,
    description:
      'The capability-based simple-role compiler crafted PolicySpec statements for a capability set and created the role in the roles service.',
    since: '1.1.0',
  });

export const ONBOARDING_JOB_COMPLETED_EVENT: EventDefinition<OnboardingJobCompletedPayload> =
  defineEvent<OnboardingJobCompletedPayload>({
    type: 'onboarding:job-completed',
    version: 1,
    schema: onboardingJobCompletedSchema,
    auditable: true,
    description:
      'The org onboarding saga finished: default store, default retail workspace, predefined roles, and owner role assignment were created.',
    since: '1.1.0',
  });

export const ONBOARDING_JOB_FAILED_EVENT: EventDefinition<OnboardingJobFailedPayload> =
  defineEvent<OnboardingJobFailedPayload>({
    type: 'onboarding:job-failed',
    version: 1,
    schema: onboardingJobFailedSchema,
    auditable: true,
    description: 'The org onboarding saga failed at a step; compensation removed the artifacts created before failing.',
    since: '1.1.0',
  });

export const DIRECTORY_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [DIRECTORY_STORE_CREATED_EVENT.type]: DIRECTORY_STORE_CREATED_EVENT,
  [DIRECTORY_WORKSPACE_CREATED_EVENT.type]: DIRECTORY_WORKSPACE_CREATED_EVENT,
  [ROLES_SIMPLE_ROLE_CREATED_EVENT.type]: ROLES_SIMPLE_ROLE_CREATED_EVENT,
  [ONBOARDING_JOB_COMPLETED_EVENT.type]: ONBOARDING_JOB_COMPLETED_EVENT,
  [ONBOARDING_JOB_FAILED_EVENT.type]: ONBOARDING_JOB_FAILED_EVENT,
};
